import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useContext } from "react";
import { AuthContext } from "src/context/AuthContext";
import { CURRENT_WORKSPACE_KEY } from "src/config/constants";

import axios from 'src/common/axiosInstance';
import { clearRequestCache } from 'src/common/requestCache';
import workspaceService from "src/services/workspaceService";

export const WorkspaceContext = createContext();

const WorkspaceProvider = ({ children }) => {
  const requestVersion = useRef(0);
  const [loading, setLoading] = useState(false);

  const [workspaces, setWorkspaces] = useState([]);

  const [currentWorkspace, setCurrentWorkspace] = useState(null);

  const { auth, currentUser, refreshSession } = useContext(AuthContext);

  /**
   * Fetch all workspaces
   */
  const fetchWorkspaces = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);

    try {
      const response = await workspaceService.index();

      if (version !== requestVersion.current) return [];
      const data = response.data.data ?? response.data;

      setWorkspaces(data);

      const savedWorkspaceId = currentUser?.local_workspace_id || (currentUser?.current_workspace?.id || (currentUser?.workspaces?.length || 0) > 1 ? null : localStorage.getItem(CURRENT_WORKSPACE_KEY));

      setCurrentWorkspace(null);
      if (savedWorkspaceId) {
        const workspace = data.find(
          (item) => String(item.id) === String(savedWorkspaceId),
        );

        if (workspace) {
          setCurrentWorkspace(workspace);
          localStorage.setItem(CURRENT_WORKSPACE_KEY, workspace.id);
        }
      }

      return data;
    } catch (error) {
      if (version === requestVersion.current) {
        setWorkspaces([]);
        setCurrentWorkspace(null);
      }
      return [];
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [currentUser?.local_workspace_id, currentUser?.current_workspace?.id, currentUser?.workspaces?.length]);

  /**
   * Select active workspace
   */
  const selectWorkspace = async (workspace) => {
    await axios.post('/api/auth/workspace', { workspace_id: workspace.id });
    clearRequestCache();
    localStorage.setItem(CURRENT_WORKSPACE_KEY, workspace.id);
    setCurrentWorkspace(workspace);
    await refreshSession();
  };

  /**
   * Refresh list
   */
  const refreshWorkspaces = () => {
    return fetchWorkspaces();
  };

  useEffect(() => {
    if (auth) {
      fetchWorkspaces();
    } else {
      setWorkspaces([]);
      setCurrentWorkspace(null);
    }
    return () => {
      requestVersion.current += 1;
    };
  }, [auth, currentUser?.id, fetchWorkspaces]);

  return (
    <WorkspaceContext.Provider
      value={{
        loading,

        workspaces,

        currentWorkspace,

        fetchWorkspaces,

        refreshWorkspaces,

        selectWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceProvider;
